import React, {useEffect, useState, useRef} from 'react';
import {connect} from 'react-redux';
import _debounce from 'lodash/debounce';
import _filter from 'lodash/filter';
import _isEqual from 'lodash/isEqual';
import _findIndex from 'lodash/findIndex';
import {startAddressSearch, selectAddress, updateSearchFilter} from '../../actions';
import {formatAddresses} from '../../selectors/addressFormat';
import PropTypes from 'prop-types';
import styles from './LocationTypeahead.scss';
import {breakpointXlLow} from '../../lib/config';
import {escapeSymbols} from '../../lib/formatting';

function CustomResultComponent(props) {
  let pattern = new RegExp(escapeSymbols(props.matchString), 'i');
  let matched = props.inputString.match(pattern);
  return matched ? <div>{props.inputString.substr(0, matched.index)}<span className={styles.selected}>{matched[0]}</span>{props.inputString.substr(matched.index + props.matchString.length)}</div> : <div>{props.inputString}</div>;
}

function LocationTypeahead(props) {
  let inputEl = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedResult, setSelectedResult] = useState(null);
  const [isDropdownShowing, setDropdownShowing] = useState(false);
  const [thisId, setInputId] = useState(null);
  const [isMobile, setIsMobile] = useState(true);
  let init = useRef(false);

  const getPrefill = () => {
    let prefill = null;
    if (props.prefillAddress) prefill = props.prefillAddress;
    return prefill;
  };

  useEffect(() => { // initialise
    if (props.hasOwnProperty('id')) setInputId(props.id);

    function toggleIsMobile() {
      setIsMobile(window.innerWidth <= breakpointXlLow ? true : false);
    }
    window.addEventListener('resize', toggleIsMobile);
    toggleIsMobile();
    return () => window.removeEventListener('resize', toggleIsMobile);
  }, []);

  useEffect(() => {
    // i no longer care about your stupid init
    if (props.prefillAddress) {
      if (!selectedResult || selectedResult && props.prefillAddress != selectedResult.pretty) {
        init.current = true;
        searchTermChange(props.prefillAddress);
        inputEl.value = props.prefillAddress;
      }
    }
  }, [props.prefillAddress]);

  useEffect(() => {
    // new address search received. filter & create searchResults. do not use this content outside of here, use searchResults
    if (props.addressSearch.status == 'success' && props.addressSearch.searchTerm == searchTerm) {
      let initing = init.current ? true : false;
      let prefill = (initing) ? getPrefill() : null;
      let newResults = processResults(props.addressSearch, prefill);
      setSearchResults(newResults.addresses);
      if (initing && newResults.bestResult) {
        newResults.bestResult.TAnoSubmit = true;
        setSelectedResult(newResults.bestResult); // side effect: it sets session. submits single. hides dropdown
      }
      setActiveIndex(newResults.activeIndex);
    }
  }, [props.addressSearch.content]);

  useEffect(() => {
    // new search results in the state. only use these for display and calc.
    let isInputFocussed = (inputEl == document.activeElement); // input won't be focussed when the component is mounting, so it's an initialisation state
    if (searchResults.length > 0 && !isDropdownShowing && isInputFocussed) setDropdownShowing(true); // show the dropdown if it's not showing and there are results
    if (searchResults.length == 0 && isDropdownShowing) setDropdownShowing(false); // hide the dropdown if it is showing and there's no results
  }, [searchResults]);

  useEffect(() => {
    if (selectedResult) {
      // New selected result put into state
      props.selectAddress({address: selectedResult});
      if (props.saveFilter) {
        props.updateSearchFilter({address: selectedResult}); // store the selected address into filters
        sessionStorage.setItem('prefillAddress', JSON.stringify(selectedResult));
      }
      if (isDropdownShowing) setDropdownShowing(false); // new selection hides the resultlist
    }
  }, [selectedResult]);

  useEffect(() => {
    // we want to enable single result submit, this should happen when a result is selected
    // we're doing it here because the parent was submitting before the filters had updated when we did it on selection
    if (!props.filterSearch.address.hasOwnProperty('TAnoSubmit')) {
      if (props.hasOwnProperty('submitSingleResult') && props.submitSingleResult == true && searchResults.length == 1) {
        callSubmit();
      }
    }
  }, [props.filterSearch.address]);

  useEffect(() => {
    if (props.overrideString) {
      inputEl.value = props.overrideString;
    }
  }, [props.overrideString]);

  const processResults = (results, prefillAddress) => {
    let matchedAddresses = results.content;
    let bestResult = null;
    let newIndex = 0;
    if (matchedAddresses && matchedAddresses.length >= 0) bestResult = matchedAddresses[newIndex];
    if (prefillAddress) {
      if (results.content.length > 0) {
        let findExact = new RegExp('^' + prefillAddress + '$', 'i');
        newIndex = _findIndex(results.content, (item) => item.formattedAddress.match(findExact));
        if (newIndex) bestResult = matchedAddresses[newIndex];
        init.current = false;
      }
    }
    return {addresses: matchedAddresses, activeIndex: newIndex, bestResult: bestResult};
  };

  const resetActiveIndex = () => {
    let matchIndex = _findIndex(searchResults, selectedResult);
    if (matchIndex >= 0 && matchIndex != activeIndex) setActiveIndex(matchIndex);
  };

  const searchTermChange = (newSearchTerm) => {
    props.startAddressSearch({
      address: newSearchTerm,
      categories: props.categories,
    });
    setSearchTerm(newSearchTerm);
  };
  const dbounceTyping = _debounce(searchTermChange, 350);

  const onChange = (event) => {
    if (props.onChange) {
      props.onChange(event?.target?.value);
    }
    let newSearchTerm = searchTerm;
    if (event && event.target && event.target.hasOwnProperty('value')) {
      newSearchTerm = event.target.value;
      // debounce typing
      dbounceTyping(newSearchTerm);
    }
    if(event.target.value == ''){
      props.updateSearchFilter({address: {}}); // store the selected address into filters
      sessionStorage.removeItem('prefillAddress');
      setSelectedResult(null);
    }
  };
  const handleKeyDown = (event) => {
    let upperIndex = searchResults.length - 1;
    switch (event.keyCode) {
      case 38: // up
        if (!isDropdownShowing) {
          // down shows a hidden dropdown
          if (searchResults.length > 0) setDropdownShowing(true);
        }
        else {
          // if activeIndex-1 is less than 0, go to end
          if (activeIndex <= 0) setActiveIndex(upperIndex);
          else setActiveIndex(activeIndex - 1);
        }
        break;
      case 40: // down
        if (!isDropdownShowing) {
          // down shows a hidden dropdown
          if (searchResults.length > 0) setDropdownShowing(true);
        }
        else {
          // if activeIndex+1 is out of bounds, reset to 1
          if (activeIndex >= upperIndex) setActiveIndex(0);
          else setActiveIndex(activeIndex + 1);
        }
        break;
      case 9: // tab
        // the user has indicated they want to select the highlighted item and move off the input
        setSelectedResult(searchResults[activeIndex]);
        if (!props.overrideString) {
          inputEl.value = searchResults[activeIndex].pretty;
        }
        break;
      case 13: // enter key
        event.preventDefault();
        break;
    }
  };
  const handleKeyUp = (event) => {
    event.preventDefault();
    switch (event.keyCode) {
      case 13: // enter key
        // user wants to select the active item, or submit the form.
        if (searchResults.length > 0) { // only if there's something to use
          if (_isEqual(selectedResult, searchResults[activeIndex])) callSubmit();
          else {
            setSelectedResult(searchResults[activeIndex]);
            if (!props.overrideString) {
              inputEl.value = searchResults[activeIndex].pretty;
            }
          }
        }
        else {
          searchTermChange(searchTerm); // fetch searchresults as a fall back
        }
        break;
      case 27: // escape key
        // reset activeIndex to the selectionIndex
        resetActiveIndex();
        // close the dropdown
        setDropdownShowing(false);
        break;
    }
  };
  const onBlur = () => {
    if (props.onBlur) {
      props.onBlur();
    }
    setDropdownShowing(false);
    resetActiveIndex();
  };
  const onFocus = () => {
    if (!isDropdownShowing && searchResults.length > 0) setDropdownShowing(true);
    inputEl.select();
  };
  const onClick = () => {
    if (!isDropdownShowing && searchResults.length > 0) setDropdownShowing(true);
  };
  const resultItemClicked = (itemIndex) => {
    // we want to set the searchItem as the selection, and do what ever that means
    setSelectedResult(searchResults[itemIndex]);
    // set the newly selected text in the input, without triggering onchange
    if (!props.overrideString) {
      inputEl.value = searchResults[itemIndex].pretty;
    }
    // focus the input
    inputEl.focus();
  };
  const callSubmit = () => {
    if (isDropdownShowing) setDropdownShowing(false);
    if (props.submitFunction) props.submitFunction();
  };

  const placeholder = props.hidePlaceholder ? '' : 'Search an address, street or suburb';
  return (
    <div className={styles[props.size]}>
      <input
        type="text"
        className={props.customFieldClass ? props.customFieldClass : styles.inputField}
        tabIndex={props.tabIndex}
        placeholder={placeholder}
        id={thisId}
        autoComplete="off"
        list="autocompleteOff"
        onClick={onClick}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={(el) => (inputEl = el)}
        aria-label="Enter Suburb" />
      {(!isMobile && props.size == 'home') ?
        <div className={'d-block rounded ' + styles.toggleFiltersButton} to="/suburb-research"  onClick={props.toggleHomeFiltersFuncion}>
          <span><i className="icon"></i>{props.showHomeFilter ? 'Hide Filters' : 'Show Filters'}</span>
        </div>
        : null}
      {isDropdownShowing ?
        <div className={styles.resultsContainer}>
          <div className={styles.resultsWrapper}>
            {searchResults.map((result, resultIndex) => {
              return <div key={resultIndex} onMouseDown={() => {resultItemClicked(resultIndex);}} onMouseOver={() => setActiveIndex(resultIndex)} className={styles.resultItem + ((resultIndex == activeIndex) ? ' ' + styles.active : '')}><CustomResultComponent matchString={searchTerm} inputString={result.pretty} /></div>
            })}
          </div>
        </div>
        : null}
    </div>
  );
}

LocationTypeahead.defaultProps = {
  size: 'normal',
  saveFilter: true,
  prefillAddress: null,
};
LocationTypeahead.propTypes = {
  id: PropTypes.string,
  size: PropTypes.string, // large, home, contactFormSize
  submitSingleResult: PropTypes.bool, // submit if there's only one result
  submitFunction: PropTypes.func, // call the parents submit function
  saveFilter: PropTypes.bool, // defaults to true, saves the selection as the search filter address. turn off for forms etc.
  prefillAddress: PropTypes.string, // address to start the typeahead with
  toggleHomeFiltersFuncion: PropTypes.func,
  showHomeFilter: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  hidePlaceholder: PropTypes.bool,
  categories: PropTypes.string,
  overrideString: PropTypes.string,
  tabIndex: PropTypes.number,
  customFieldClass: PropTypes.string,
};

const mapStateToProps = (state) => ({
  addressSearch: formatAddresses(state.addressSearch),
  filterSearch: state.filterSearch,
  selectedAddress: state.addressSelected,
});
const mapDispatchToProps = {startAddressSearch, selectAddress, updateSearchFilter};
export default connect(mapStateToProps, mapDispatchToProps)(LocationTypeahead);
